import "./CategoryFlyoutMenu.scss";
import { useCallback, useRef, useEffect, Fragment, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../store";
import { isRouteActive } from "../../../common/util/Navigation/routeMatching";
import defaultContent from "../../../content/navigation";
import defaultCommonContent from "../../../content/common";

export interface FlyoutMenuItem {
  id: string;
  contentKey?: string;
  label: string;
  path?: string;
  children?: FlyoutMenuItem[];
  isSection?: boolean;
}

interface CategoryFlyoutMenuProps {
  title: string;
  items: FlyoutMenuItem[];
  position: { top: number; left: number };
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  triggerButtonRef?: HTMLButtonElement | HTMLAnchorElement | null;
  openedViaKeyboard?: boolean;
}

export const CategoryFlyoutMenu = ({
  title,
  items,
  position,
  onClose,
  onMouseEnter,
  onMouseLeave,
  triggerButtonRef,
  openedViaKeyboard = false,
}: CategoryFlyoutMenuProps) => {
  const { t } = useTranslation(["navigation", "common"]);
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);
  const isFirstItemAssignedRef = useRef(false);
  const { pathname, hash } = useLocation();

  const disclaimerSymbol = t("disclaimerSymbol", defaultCommonContent["disclaimerSymbol"], { ns: "common" });

  // Cache focusable elements outside the menu for performance (avoid querying on every Tab press)
  const focusableElementsOutsideMenu = useMemo(() => {
    return Array.from(
      document.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !menuRef.current?.contains(el));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;

      // Don't close if clicking/touching inside the menu
      if (menuRef.current?.contains(target)) {
        return;
      }

      // Don't close if clicking/touching the trigger button when it exists
      if (triggerButtonRef?.contains(target)) {
        return;
      }

      onClose();
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener("touchend", handleClickOutside, true);
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("touchend", handleClickOutside, true);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [onClose, triggerButtonRef]);

  useEffect(() => {
    // Focus first menu item when flyout opens via keyboard (not hover)
    if (openedViaKeyboard && firstItemRef.current) {
      firstItemRef.current.focus();
    }
  }, [openedViaKeyboard]);

  /**
   * Keyboard navigation handler for flyout menu (ARIA menu pattern).
   *
   * Implements WAI-ARIA menu keyboard interaction:
   * - ArrowDown/ArrowUp: Navigate between menu items (roving tabindex pattern)
   * - Home/End: Jump to first/last menu item
   * - Escape: Close menu and return focus to trigger button
   * - Tab/Shift+Tab: Close menu and move focus to next/previous focusable element in page order
   *
   * The Tab behavior maintains natural document focus flow:
   * - Forward Tab: Closes menu and focuses next element after trigger button
   * - Backward Tab: Closes menu and returns focus to trigger button
   *
   * Note: event.detail is used elsewhere to detect keyboard vs. mouse activation.
   * This is a standard browser API where detail=0 for keyboard events and detail>0 for clicks.
   *
   * @see https://www.w3.org/WAI/ARIA/apg/patterns/menu/
   * @see https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const menuItems = menuRef.current?.querySelectorAll<HTMLAnchorElement>('a[role="menuitem"]');
      if (!menuItems?.length) return;

      const currentIndex = Array.from(menuItems).findIndex((item) => item === document.activeElement);

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          if (currentIndex < 0) {
            // No item focused, focus first
            menuItems[0].focus();
          } else {
            // Move to next item (wrap to first if at end)
            const nextIndex = (currentIndex + 1) % menuItems.length;
            menuItems[nextIndex].focus();
          }
          break;

        case "ArrowUp":
          event.preventDefault();
          if (currentIndex < 0) {
            // No item focused, focus last
            menuItems[menuItems.length - 1].focus();
          } else {
            // Move to previous item (wrap to last if at beginning)
            const prevIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
            menuItems[prevIndex].focus();
          }
          break;

        case "Home":
          event.preventDefault();
          menuItems[0].focus();
          break;

        case "End":
          event.preventDefault();
          menuItems[menuItems.length - 1].focus();
          break;

        case "Escape":
          event.preventDefault();
          onClose();
          // Return focus to the trigger button
          if (triggerButtonRef) {
            triggerButtonRef.focus();
          }
          break;

        case "Tab":
          {
            if (triggerButtonRef) {
              const triggerIndex = focusableElementsOutsideMenu.indexOf(triggerButtonRef);

              if (event.shiftKey) {
                // Shift+Tab: go back to trigger button
                event.preventDefault();
                onClose();
                triggerButtonRef.focus();
              } else {
                // Tab forward: go to next element after trigger
                event.preventDefault();
                onClose();
                const nextIndex = triggerIndex + 1;
                if (nextIndex < focusableElementsOutsideMenu.length) {
                  focusableElementsOutsideMenu[nextIndex].focus();
                } else {
                  // No next element, go to trigger
                  triggerButtonRef.focus();
                }
              }
            } else {
              // Fallback: just close and let browser handle it
              onClose();
            }
          }
          break;
      }
    };

    const handleFocusOut = (_event: FocusEvent) => {
      // Close menu when focus leaves it entirely
      // Use setTimeout to let the browser complete the focus change first
      setTimeout(() => {
        if (menuRef.current && !menuRef.current.contains(document.activeElement)) {
          onClose();
        }
      }, 0);
    };

    const menuElement = menuRef.current;
    if (menuElement) {
      menuElement.addEventListener("keydown", handleKeyDown);
      menuElement.addEventListener("focusout", handleFocusOut);
    }

    return () => {
      if (menuElement) {
        menuElement.removeEventListener("keydown", handleKeyDown);
        menuElement.removeEventListener("focusout", handleFocusOut);
      }
    };
  }, [onClose, triggerButtonRef, focusableElementsOutsideMenu]);

  const handleItemClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const renderMenuItem = (item: FlyoutMenuItem, isSubItem = false) => {
    const linkID = item.contentKey ?? item.id;

    if (item.isSection) {
      return (
        <Fragment key={item.id}>
          <div className="CategoryFlyoutMenu__section-header">{t(`linkTitle.${linkID}`, item.label)}</div>
          {item.children && item.children.length > 0 && (
            <>{item.children.map((child) => renderMenuItem(child, true))}</>
          )}
        </Fragment>
      );
    }

    const isActive = isRouteActive(item.path, pathname, hash);
    const translatedLabel = t(`linkTitle.${linkID}`, item.label, { symbol: disclaimerSymbol });
    const shouldGetRef = !isFirstItemAssignedRef.current;
    if (shouldGetRef) {
      isFirstItemAssignedRef.current = true;
    }

    const [itemPathname, itemHash] = (item.path ?? "").split("#");

    return (
      <Link
        key={item.id}
        ref={shouldGetRef ? firstItemRef : null}
        to={{ pathname: itemPathname, hash: itemHash ? `#${itemHash}` : undefined }}
        role="menuitem"
        className={`CategoryFlyoutMenu__item ${isSubItem ? "CategoryFlyoutMenu__item--sub" : ""} ${isActive ? "CategoryFlyoutMenu__item--active" : ""}`}
        data-testid={`flyout-item-${item.id}`}
        onClick={handleItemClick}
      >
        {translatedLabel}
      </Link>
    );
  };

  // Calculate max-height to prevent overflow beyond viewport
  const maxHeight = `calc(100vh - ${position.top}px - 1rem)`;

  return (
    <div
      ref={menuRef}
      className="CategoryFlyoutMenu"
      style={{ top: `${position.top}px`, left: `${position.left}px`, maxHeight }}
      role="menu"
      tabIndex={0}
      aria-label={`${title} menu`}
      data-testid="category-flyout-menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="CategoryFlyoutMenu__header">
        <h2 className="CategoryFlyoutMenu__title">{title}</h2>
      </div>
      <div className="CategoryFlyoutMenu__content">{items.map((item) => renderMenuItem(item))}</div>
    </div>
  );
};
